// Where each kind of recipient's address and notification preference actually live.
// This used to be a two-way ternary (`recipientType === 'worker' ? 'workers' : 'employers'`),
// which silently sent every non-worker to `employers`. Messaging already passes 'union', so
// union recipients were looked up in the wrong table, matched nothing, and were dropped by the
// preference guard below -- a send that failed invisibly. An unknown type now bails out
// explicitly rather than defaulting into somebody else's table.
const RECIPIENTS = {
  worker: {
    table:  'workers',
    select: 'email, email_notifications, first_name, last_name',
    name:   r => `${r.first_name || ''} ${r.last_name || ''}`.trim(),
  },
  employer: {
    table:  'employers',
    select: 'email, email_notifications, company_name',
    name:   r => r.company_name || '',
  },
  union: {
    table:  'union_accounts',
    select: 'email, email_notifications, union_name',
    name:   r => r.union_name || '',
  },
};

// Fans a union notification out to every ACTIVE staff seat rather than the owner alone.
// The sending client is normally a worker, who cannot read union_staff or auth.users, and the
// notifications INSERT policy only admits the union's own account id -- so both the in-app rows
// and the staff addresses come from notify_union_staff(), a SECURITY DEFINER function that
// first checks the caller has a real conversation or application with this union. It writes
// one notifications row per active seat and returns those seats' emails; deactivated seats are
// excluded there, so neither channel can drift from the other.
//
// `inAppType` is the label stored on the notification row (the bell reads only `message` and
// `link_to`, so it stays as it was); `emailType` selects the email template, which is not
// always the same name -- an apprenticeship application is 'new_interest' in-app but
// 'apprenticeship_interest' by email.
export async function notifyUnionStaff(supabase, { unionId, inAppType, emailType, message, linkTo, ...payload }) {
  try {
    if (!unionId) return;

    const { data: staff, error } = await supabase.rpc('notify_union_staff', {
      p_union_id: unionId,
      p_type:     inAppType,
      p_message:  message,
      p_link_to:  linkTo,
    });
    if (error || !staff?.length) return;

    // The in-app rows above are written either way; this account-wide flag governs only the
    // emails. Kept union-wide rather than per-seat, by product decision.
    const { data: account } = await supabase
      .from('union_accounts')
      .select('email_notifications, union_name')
      .eq('id', unionId)
      .single();
    if (!account?.email_notifications) return;

    await Promise.all(
      staff.filter(s => s.email).map(s =>
        fetch('/api/notify-interest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type:            emailType,
            recipient_email: s.email,
            recipient_name:  account.union_name || '',
            recipient_kind:  'union',
            ...payload,
          }),
        }).catch(() => {})
      )
    );
  } catch (_) {}
}

export async function sendNotificationEmail(supabase, { recipientId, recipientType, type, ...payload }) {
  try {
    const target = RECIPIENTS[recipientType];
    if (!target || !recipientId) return;

    const { data: recipient } = await supabase
      .from(target.table)
      .select(target.select)
      .eq('id', recipientId)
      .single();
    if (!recipient?.email_notifications || !recipient?.email) return;

    await fetch('/api/notify-interest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type,
        recipient_email: recipient.email,
        recipient_name:  target.name(recipient),
        // Lets templates that serve more than one audience point their CTA and their
        // "Manage preferences" link at the right dashboard.
        recipient_kind:  recipientType,
        ...payload,
      }),
    });
  } catch (_) {}
}
